/**
 * ㉘ 사실 정합(Node 16:20 · docs/audit/report_fact_20261006.md md5 f32b910d…) — 화면·글이 «근거 있는 값만» 쓰게 하는 공용 규칙(순수 함수).
 *
 *  ① displayDong   — apt_sites.dong 은 dong_source 가 NULL 이면 표시하지 않는다(구까지만). 틀린 동보다 빈 동이 낫다.
 *  ② josaIGa       — 「{시공사}가/이 시공합니다」 의 이/가 를 받침으로 고른다(판정 불가면 null → 호출부는 조사 없는 문형).
 *  ③ unitsLabel    — 세대수 라벨: complex_units 가 있으면 「총 N세대(공급 M)」, 없으면 「공급 M세대」만. «총 N세대» 에 공급 세대를 넣지 않는다.
 *  ④ transferLimitYears — 전매제한 년수: 2023-04-07 이후 공고는 법정 최대 3년 — 3 초과(또는 비정상)면 null(숨김).
 * 모든 렌더 경로(상세·허브·OG·SP·토론방·블로그·카페)는 이 함수들만 쓴다 — 각자 흉내 내지 않는다.
 */

// ── ① 동 ────────────────────────────────────────────────────────────────────
export interface DongLike { dong?: string | null; dong_source?: string | null }
/** 근거(dong_source)가 있는 동만 돌려준다. 없거나 빈 값이면 null. */
export function displayDong(site: DongLike | null | undefined): string | null {
  if (!site) return null;
  const src = String(site.dong_source ?? '').trim();
  const dong = String(site.dong ?? '').trim();
  return src && dong ? dong : null;
}

// ── ② 이/가 ─────────────────────────────────────────────────────────────────
const DIGIT_BATCHIM: Record<string, boolean> = { '0': true, '1': true, '2': false, '3': true, '4': false, '5': false, '6': true, '7': true, '8': true, '9': false };
// 영문 약어를 한글로 읽을 때의 끝소리(에이·비·씨·디·이·에프·지·에이치·아이·제이·케이·엘·엠·엔·오·피·큐·알·에스·티·유·브이·더블유·엑스·와이·지)
const LETTER_BATCHIM: Record<string, boolean> = { A: false, B: false, C: false, D: false, E: false, F: true, G: false, H: false, I: false, J: false, K: false, L: true, M: true, N: true, O: false, P: false, Q: false, R: true, S: false, T: false, U: false, V: false, W: false, X: true, Y: false, Z: false };
const TAIL_NOISE = /[\s)\]}>」』”’'".,·\-_/]+$/;

/** 끝말의 받침 유무. 한글=종성 검사, 숫자·영문 약어=읽는 소리, 그 밖(기호만 등)은 null(판정 불가). */
export function hasBatchim(word: string | null | undefined): boolean | null {
  let w = String(word ?? '').replace(/\((주|유|사)\)|㈜|주식회사|\(주식회사\)/g, '').replace(TAIL_NOISE, '').trim();
  if (!w) return null;
  const ch = Array.from(w).pop()!;
  const code = ch.charCodeAt(0);
  if (code >= 0xac00 && code <= 0xd7a3) return (code - 0xac00) % 28 !== 0;
  if (ch in DIGIT_BATCHIM) return DIGIT_BATCHIM[ch];
  const up = ch.toUpperCase();
  if (up in LETTER_BATCHIM) return LETTER_BATCHIM[up];
  return null;
}
/** 주격 조사 — 받침 있으면 「이」, 없으면 「가」, 판정 불가면 null. */
export function josaIGa(word: string | null | undefined): '이' | '가' | null {
  const b = hasBatchim(word);
  return b == null ? null : b ? '이' : '가';
}
/** 「{word}이/가」 — 판정 불가면 「{word}은(는) 아닌」 같은 말 대신 null 을 돌려 호출부가 조사 없는 문형을 고르게 한다. */
export function withIGa(word: string | null | undefined): string | null {
  const j = josaIGa(word);
  return j == null || !word ? null : `${String(word).trim()}${j}`;
}

// ── ③ 세대수 ────────────────────────────────────────────────────────────────
const posInt = (v: unknown): number | null => { const n = Number(v); return Number.isFinite(n) && n > 0 ? Math.round(n) : null; };
export interface UnitsLike { complex_units?: number | null; supply_units?: number | null }
const nf = (n: number) => n.toLocaleString('ko-KR');
/**
 * 세대수 라벨.
 *   complex(단지 총세대)·supply(이번 공급) 둘 다 → 「총 N세대(공급 M)」 · complex 만 → 「총 N세대」 · supply 만 → 「공급 M세대」 · 둘 다 없으면 null.
 *   공급이 단지 총세대보다 크면(= 앞 값을 못 믿는다) 「공급 M세대」 만 쓴다.
 *   ⛔ «총 N세대» 자리에 supply_units(또는 그와 같은 total_units)를 넣지 않는다 — 그건 공급이다.
 */
export function unitsLabel(u: UnitsLike | null | undefined): string | null {
  const c = posInt(u?.complex_units); const s = posInt(u?.supply_units);
  if (c && s && s <= c) return `총 ${nf(c)}세대(공급 ${nf(s)})`;
  if (c && s) return `공급 ${nf(s)}세대`;
  if (c) return `총 ${nf(c)}세대`;
  if (s) return `공급 ${nf(s)}세대`;
  return null;
}
/** 문장용 — 「총 N세대 규모」 가 맞을 때만 단지 규모 문장을 만든다(complex 있을 때). 아니면 null. */
export function complexScaleLabel(u: UnitsLike | null | undefined): string | null {
  const c = posInt(u?.complex_units);
  return c ? `총 ${nf(c)}세대 규모` : null;
}

/**
 * description 생성용 세대 문구(문장 머리 — 뒤에 「로 계획되어 있습니다.」 를 붙인다).
 *   complex_units>0 → 「총 C세대 규모」 + (supply>0 이고 supply≠C 면 「(이번 공급 S세대)」)
 *   complex 없음·supply>0 → 「공급 S세대」
 *   둘 다 없음 → null(문장을 만들지 않는다).
 *   ⛔ «총» 자리에 supply(total_units)를 넣지 않는다 — 그건 공급이다.
 */
export function descUnitsPhrase(u: UnitsLike | null | undefined): string | null {
  const c = posInt(u?.complex_units); const sup = posInt(u?.supply_units);
  if (c) return `총 ${nf(c)}세대 규모${sup && sup !== c ? `(이번 공급 ${nf(sup)}세대)` : ''}`;
  if (sup) return `공급 ${nf(sup)}세대`;
  return null;
}

/**
 * 입주 YYYYMM → 「YYYY년 M월」. 숫자가 섞인 아무 표기(`202612` · `2026-12` · `2026.12.01`)를 받되
 *   6자리를 못 채우거나 월이 1~12 가 아니면 null — 원문(6자리)을 되살리지 않는다.
 */
export function moveInYm(raw: string | number | null | undefined): string | null {
  const digits = String(raw ?? '').replace(/[^0-9]/g, '');
  if (digits.length < 6) return null;
  const y = Number(digits.slice(0, 4)); const m = Number(digits.slice(4, 6));
  if (!y || !m || m > 12) return null;
  return `${y}년 ${m}월`;
}

/**
 * description 입주 문장 — 지난 달이면 「2025년 5월 입주.」 · 아니면 「2028년 3월 입주 예정.」 · 못 읽으면 null.
 *   `past` 는 호출부가 schedule.ts 의 moveInPast(월말 기준 · KST 오늘)로 판정해 넘긴다(판정은 그 한 곳).
 */
export function moveInSentence(raw: string | number | null | undefined, past: boolean): string | null {
  const ym = moveInYm(raw);
  if (!ym) return null;
  return past ? `${ym} 입주.` : `${ym} 입주 예정.`;
}

// ── ④ 전매제한 ──────────────────────────────────────────────────────────────
/** 개정 주택법 시행(2023-04-07) 이후 공고의 법정 최대 전매제한 — 3년. */
export const TRANSFER_LIMIT_CUTOFF = '2023-04-07';
export const TRANSFER_LIMIT_MAX_YEARS = 3;
/**
 * 화면에 낼 전매제한 년수. 숨길 값이면 null.
 *   · 값이 양수가 아니거나 비정상(> 20)이면 null.
 *   · 공고일이 2023-04-07 이후(또는 모르면)이고 3년을 넘으면 null — 법적으로 불가능한 값(공고 파서의 「6개월」→6년 오독 등).
 *   · 공고일이 그 전이면 옛 규정(최대 10년)까지는 그대로.
 * noticeDate: 'YYYY-MM-DD' 또는 'YYYYMMDD' 또는 null.
 */
export function transferLimitYears(years: number | string | null | undefined, noticeDate?: string | null): number | null {
  const y = Number(years);
  if (!Number.isFinite(y) || y <= 0 || y > 20) return null;
  const d = String(noticeDate ?? '').replace(/[^0-9]/g, '').slice(0, 8);
  const iso = d.length === 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : null;
  const afterCutoff = iso == null ? true : iso >= TRANSFER_LIMIT_CUTOFF;
  if (afterCutoff && y > TRANSFER_LIMIT_MAX_YEARS) return null;
  if (!afterCutoff && y > 10) return null;
  return y;
}
/**
 * 공고 문구의 전매제한 기간 → 년수(소수 허용). 「6개월」 → 0.5 · 「1년 6개월」 → 1.5 · 「3년」 → 3 · 「없음/해당없음」 → 0(제한 없음) · 읽지 못하면 null.
 * 단위를 «반드시» 읽는다 — 숫자만 읽어 개월을 년으로 착각하지 않는다(F5).
 */
export function parseTransferLimitYears(text: string | null | undefined): number | null {
  const t = String(text ?? '').replace(/\s+/g, '');
  if (!t) return null;
  if (/(전매제한)?(없음|해당없음|미해당)/.test(t) && !/\d/.test(t)) return 0;
  const m = /(?:(\d+(?:\.\d+)?)년)?(?:(\d+)개월)?/.exec(t.replace(/전매제한/g, ''));
  const hasUnit = /(\d+(?:\.\d+)?)년|(\d+)개월/.test(t);
  if (!hasUnit || !m) return null;
  const y = m[1] ? Number(m[1]) : 0; const mo = m[2] ? Number(m[2]) : 0;
  const v = y + mo / 12;
  return v > 0 ? Math.round(v * 100) / 100 : null;
}

// ── ④-b 전매제한 «개월» 단위 (next-33) ────────────────────────────────────────
//   apt_subscriptions.transfer_limit_months(정수 개월)가 «진실» 이고, transfer_limit_years(정수 열)는 months/12 반올림 정수로 병행 유지한다.
//   ⛔ years 로는 「6개월」 을 못 담는다(정수 열) — 읽는 쪽은 months 를 먼저 보고, 없을 때만 years×12 로 내려간다.
/** 법정 최대 전매제한 — 2023-04-07 이후 공고 36개월. 그 전 공고는 옛 규정(최대 10년 = 120개월). */
export const TRANSFER_LIMIT_MAX_MONTHS = 36;
export const TRANSFER_LIMIT_MAX_MONTHS_LEGACY = 120;
const posMonths = (n: unknown): number | null => { const v = Number(n); return Number.isInteger(v) && v > 0 ? v : null; };
/** months → years 열에 병행 저장할 정수(반올림 · 6개월 → 1). 읽는 쪽이 이 값으로 표기하면 안 된다. */
export function monthsToYearsInt(months: number): number { return Math.round(months / 12); }
/**
 * 공고 문구 → 전매제한 «개월». 「6개월」 → 6 · 「1년 6개월」 → 18 · 「3년」 → 36 · 「없음/해당없음」 → 0(제한 없음) · 읽지 못하면 null.
 * 단위를 «반드시» 읽는다(숫자만으로 추정하지 않는다).
 */
export function parseTransferLimitMonths(text: string | null | undefined): number | null {
  const y = parseTransferLimitYears(text);
  return y == null ? null : Math.round(y * 12);
}
/**
 * 화면에 낼 전매제한 개월. 숨길 값이면 null.
 *   · 양의 정수가 아니면 null. 공고일이 2023-04-07 이후(또는 모르면)이고 36개월을 넘으면 null — 법적으로 불가능한 값.
 *   · 공고일이 그 전이면 옛 규정(최대 120개월)까지.
 */
export function transferLimitMonths(months: number | string | null | undefined, noticeDate?: string | null): number | null {
  const m = posMonths(months);
  if (m == null) return null;
  const d = String(noticeDate ?? '').replace(/[^0-9]/g, '').slice(0, 8);
  const iso = d.length === 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : null;
  const afterCutoff = iso == null ? true : iso >= TRANSFER_LIMIT_CUTOFF;
  if (afterCutoff && m > TRANSFER_LIMIT_MAX_MONTHS) return null;
  if (!afterCutoff && m > TRANSFER_LIMIT_MAX_MONTHS_LEGACY) return null;
  return m;
}
/** 행의 전매제한 개월 — months 가 있으면 그 값, 없으면 years×12(정수 년만). 그 뒤 transferLimitMonths 가드. */
export function effectiveTransferMonths(
  row: { transfer_limit_months?: number | null; transfer_limit_years?: number | null } | null | undefined,
  noticeDate?: string | null,
): number | null {
  if (!row) return null;
  const direct = posMonths(row.transfer_limit_months);
  const yrs = posMonths(row.transfer_limit_years);
  const m = direct ?? (yrs != null ? yrs * 12 : null);
  return transferLimitMonths(m, noticeDate);
}
/** 표기 — 6 → 「6개월」 · 12 → 「1년」 · 18 → 「1년 6개월」 · 36 → 「3년」. 양의 정수가 아니면 null. */
export function transferLimitLabel(months: number | null | undefined): string | null {
  const m = posMonths(months);
  if (m == null) return null;
  if (m < 12) return `${m}개월`;
  const y = Math.floor(m / 12); const r = m % 12;
  return r === 0 ? `${y}년` : `${y}년 ${r}개월`;
}
